import { v4 as uuidv4 } from "uuid";

import { isActionType, isRootSequence, isWaypoint } from "./TypeGuards";

import type { Action, ActionArgs, Mission } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";
import type { ActionType, NodeIndexMap, NodePath, Waypoint } from "./types";

export type RootSearchResult = {
  action: Action;
  path: NodePath;
};
export type NonRootSearchResult = RootSearchResult & {
  actionIdx: number;
  parentSequence: ActionType<"sequence">;
};

type SearchResult = RootSearchResult | NonRootSearchResult;

type NodeId = string;

class MissionException extends Error {
  missionState: Mission;

  constructor(message: string, missionState: Mission) {
    super(message);
    this.missionState = missionState;
    console.error({
      error: message,
      missionState,
    });
  }
}

/**
 * Shallow merge two objects with controlled null/undefined handling
 * @param object base object
 * @param partial partial object to merge
 * @param settings copy control settings
 * @returns merged object
 */
function shallowMerge<T extends object, P extends object = Record<string, unknown>>(
  object: T,
  partial: P,
  settings?: { copyNulls?: boolean; copyUndefineds?: boolean }
): T {
  const output = { ...object };
  for (const key in partial) {
    const val = partial[key];
    if (val === null && settings?.copyNulls !== true) {
      continue;
    }
    if (val === undefined && settings?.copyUndefineds !== true) {
      continue;
    }
    if (output.hasOwnProperty(key)) {
      (output as Record<string, unknown>)[key] = val;
    }
  }
  return output;
}

export function isNonRootSearchResult(result: SearchResult): result is NonRootSearchResult {
  return result.hasOwnProperty("actionIdx") && result.hasOwnProperty("parentSequence");
}

export function getActionForPath(actionList: Action[], path: NodePath): SearchResult | undefined {
  if (path.length === 0) return;
  let pathIdx = 0;
  let actionIdx = path[pathIdx++];
  let action = actionList[actionIdx];
  let parentSequence;
  // recursively read through the nested sequences
  while (action && isActionType(action, "sequence") && pathIdx < path.length) {
    parentSequence = action;
    actionIdx = path[pathIdx++];
    action = action.args.sequence.actions[actionIdx];
  }
  // we didn't go all the way through the path, so log an error. We have an invalid path.
  if (pathIdx < path.length || !action) {
    console.error(`Invalid path ${path} for action list ${JSON.stringify(actionList)}!`);
    return;
  }
  if (isRootSequence(action)) {
    return {
      action,
      path,
    };
  }
  return {
    action,
    path,
    actionIdx,
    parentSequence,
  };
}

/**
 * Breadth first search on the actions tree using a filter callback
 * @param mission mission object
 * @param filter function for searching actions
 * @param limit returns up to this many results. If undefined returns all results
 * @returns Array of found actions, their parent (if any, root has none), their sequence index if any
 */
export function findActions(
  mission: Mission,
  filter: (action: Action) => boolean,
  limit?: number
): SearchResult[] {
  if (mission.actions.length === 0 || !isRootSequence(mission.actions[0])) {
    // mission not loaded yet or invalid format, can't find anything.
    return [];
  }
  return findInSequence(mission.actions[0], [0], {
    filter,
    limit,
  });
}

/**
 * Search a specific sub sequence of the full mission tree. Faster than a full
 * findActions call for any sequence other than the root
 * @param parentSequence a sequence
 * @param parentSequencePath the node path to that sequence
 * @param filter filtering function
 * @param limit how many results to find before exiting. Optional
 * @param startFromIdx
 */
export function findInSequence(
  sequence: ActionType<"sequence">,
  pathToSequence: NodePath,
  params?: {
    filter?: (action: Action) => boolean;
    limit?: number;
    startFromIdx?: number;
  }
): SearchResult[] {
  const output = [];
  const basePath = [...pathToSequence];
  const { filter, limit, startFromIdx } = params ?? {};
  if (!filter || filter(sequence)) {
    output.push({ action: sequence, path: basePath });
  }
  let parentSequence = sequence;
  const queue = sequence.args.sequence.actions
    .slice(startFromIdx ?? 0)
    .map((action, actionIdx) => ({
      action,
      parentSequence,
      // index offset in case we didn't start from 0
      path: [...basePath, actionIdx + (startFromIdx ?? 0)],
      actionIdx: actionIdx + (startFromIdx ?? 0),
    }));
  while (queue.length > 0) {
    if (output.length === limit) {
      return output;
    }
    const node = queue.shift()!;
    if (!filter || filter(node.action)) {
      output.push(node);
    }
    if (isActionType(node.action, "sequence")) {
      parentSequence = node.action;
      queue.push(
        // no-loop-func rule prevents modifying a variable from outside the loop in the loop
        // however this is the correct way to linearize a BFS so I'm supressing the error

        ...node.action.args.sequence.actions.map((action, actionIdx) => ({
          action,
          parentSequence,
          actionIdx,
          path: [...node.path, actionIdx],
        }))
      );
    }
  }
  return output;
}

/**
 * helper function for finding just the first action that meets a certain filter
 * @param mission mission object
 * @param filter function for searching actions
 * @returns first match for filter in mission or undefined
 */
export function findFirstAction(
  mission: Mission,
  filter: (action: Action) => boolean
): SearchResult | null {
  return findActions(mission, filter, 1)[0];
}

/**
 * Get the previous action in the parent sequence
 * @param action the action to get the previous action of
 * @param mission the mission object
 * @param nodePathMap the node path map from the mission editor store
 * @returns the previous action in the parent sequence or null if there is no previous action
 */
export function getPreviousAction(
  action: Action,
  mission: Mission,
  nodePathMap: NodeIndexMap
): Action | null {
  const pathToAction = Array.from(nodePathMap[action.actionUuid]);
  const actionSearch = getActionForPath(mission.actions, pathToAction);
  if (!actionSearch || !isNonRootSearchResult(actionSearch)) {
    // invalid action path, either got no result or the root. Either way there is no parent sequence
    // so there is nothing to find
    return null;
  }
  const idx = actionSearch.actionIdx - 1;
  if (idx < 0) {
    return null;
  }
  return actionSearch.parentSequence.args.sequence.actions[idx];
}

/**
 * Get the next action in the parent sequence
 * @param action the action to get the next action of
 * @param mission the mission object
 * @param nodePathMap the node path map from the mission editor store
 * @returns the next action in the parent sequence or null if there is no next action
 */
export function getNextAction(
  action: Action,
  mission: Mission,
  nodePathMap: NodeIndexMap
): Action | null {
  const pathToAction = Array.from(nodePathMap[action.actionUuid]);
  const actionSearch = getActionForPath(mission.actions, pathToAction);
  if (!actionSearch || !isNonRootSearchResult(actionSearch)) {
    return null;
  }
  const idx = actionSearch.actionIdx + 1;
  if (idx >= actionSearch.parentSequence.args.sequence.actions.length) {
    return null;
  }
  return actionSearch.parentSequence.args.sequence.actions[idx];
}

/**
 * Given an action A that belongs to sequence S iterate in the specified direction through S starting from idx A +/- 1
 * until we find another action B such that depth(A) == depth(B) and filter(B) = true.
 * @param action some action that is not the root of the action tree
 * @param mission reference to the mission spec
 * @param nodePathMap node path map from the mission editor store
 * @param filter filter function for matching actions. Won't be run on input action
 * @param direction whether to iterate forwards or backwards from Action
 */
export function findSiblingAction(
  action: Action,
  mission: Mission,
  nodePathMap: NodeIndexMap,
  filter: (action: Action) => boolean,
  direction: "forward" | "backward"
): NonRootSearchResult | null {
  const pathToAction = Array.from(nodePathMap[action.actionUuid]);
  const actionSearch = getActionForPath(mission.actions, pathToAction);
  if (!actionSearch || !isNonRootSearchResult(actionSearch)) {
    // invalid action path, either got no result or the root. Either way there is no parent sequence
    // so there is nothing to find
    return null;
  }
  const { sequence } = actionSearch.parentSequence.args;
  // don't include the action we just looked up in the search
  let idx = actionSearch.actionIdx + (direction === "forward" ? 1 : -1);
  while (direction === "forward" ? idx < sequence.actions.length : idx >= 0) {
    const candidate = actionSearch.parentSequence.args.sequence.actions[idx];
    if (filter(candidate)) {
      return {
        action: candidate,
        actionIdx: idx,
        parentSequence: actionSearch.parentSequence,
        path: Array.from(nodePathMap[candidate.actionUuid]),
      };
    }
    idx += direction === "forward" ? 1 : -1;
  }

  return null;
}

/**
 * helper function for determining if an action meeting a certain filter exists in the spec.
 * More efficient than calling findActions as it will stop after 1 result is found.
 * @param mission mission object
 * @param filter function for searching actions
 * @returns boolean
 */
export function hasAction(mission: Mission, filter: (action: Action) => boolean): boolean {
  return !!findActions(mission, filter, 1)[0];
}

/**
 * Mission.proto spec has an actions list, this is a bit misleading. It must always be of length
 * 1 where the action at idx = 0 is a root sequence. This is a utility function that does a hard
 * validation of that assumption and returns the contents of that root sequence.
 * @param mission Mission object
 * @returns list of action objects
 */
export function getRootSequence(mission: Mission): {
  actions: Action[];
  rootSequenceUuid: NodeId;
} {
  const firstAction = mission.actions[0];
  if (!isRootSequence(firstAction)) {
    throw new Error(
      `Invalid mission spec! root sequence is not the top level action! ${JSON.stringify(mission)}`
    );
  }
  return {
    actions: firstAction.args.sequence.actions,
    rootSequenceUuid: firstAction.actionUuid,
  };
}

/**
 * A waypoint is just a very specific kind of sequence. See TypeGuards Waypoint class for exact spec.
 * This utility method makes it a bit easier to think about without having to memorize action indecies and nullabilities.
 *
 * @param waypoint
 * @returns waypoint actions
 */
export function getWaypointActions({
  args: {
    sequence: { actions },
  },
}: Waypoint): {
  setObstacleAvoidance: ActionType<"setObstacleAvoidance">;
  startVideo: ActionType<"startVideo"> | undefined;
  stopVideo: ActionType<"stopVideo"> | undefined;
  photoIntervalToggle: ActionType<"togglePhotoIntervalMode"> | undefined;
  position: ActionType<"gotoWaypoint">;
  timedWait: ActionType<"timedWait"> | undefined;
  photo: ActionType<"takePhoto"> | undefined;
  pano: ActionType<"takePanorama"> | undefined;
  postGotoPhotoIntervalToggle: ActionType<"togglePhotoIntervalMode"> | undefined;
  obstacleAvoidanceReset: ActionType<"setObstacleAvoidance">;
} {
  let timedWait, photoAction, postGotoPhotoIntervalToggle;
  const oaReset = actions.at(-1);

  if (!oaReset || !isActionType(oaReset, "setObstacleAvoidance")) {
    throw new Error(
      `Invalid waypoint, the obstacle avoidance reset is missing ${JSON.stringify(actions)}`
    );
  }

  if (actions[6]) {
    timedWait = actions[3];
    postGotoPhotoIntervalToggle = actions[4];
    photoAction = actions[5];
  } else if (actions[5]) {
    timedWait = isActionType(actions[3], "timedWait") ? actions[3] : undefined;
    postGotoPhotoIntervalToggle = timedWait ? actions[4] : actions[3];
    photoAction = actions[4];
  } else if (actions[4]) {
    postGotoPhotoIntervalToggle = actions[3];
    photoAction = actions[3];
    timedWait = actions[3];
  }
  return {
    setObstacleAvoidance: actions[0],
    startVideo: isActionType(actions[1], "startVideo") ? actions[1] : undefined,
    stopVideo: isActionType(actions[1], "stopVideo") ? actions[1] : undefined,
    photoIntervalToggle: isActionType(actions[1], "togglePhotoIntervalMode")
      ? actions[1]
      : undefined,
    position: actions[2],
    timedWait: timedWait && isActionType(timedWait, "timedWait") ? timedWait : undefined,
    photo: photoAction && isActionType(photoAction, "takePhoto") ? photoAction : undefined,
    pano: photoAction && isActionType(photoAction, "takePanorama") ? photoAction : undefined,
    postGotoPhotoIntervalToggle:
      postGotoPhotoIntervalToggle &&
      isActionType(postGotoPhotoIntervalToggle, "togglePhotoIntervalMode")
        ? postGotoPhotoIntervalToggle
        : undefined,
    obstacleAvoidanceReset: oaReset,
  };
}

/**
 * A mission is a list of actions. Each action can have sub actions. The resulting structure is a tree.
 *
 * To enable O(1) updates we pre-compute every possible path through that tree and store it in our
 * store as _nodeIndexMap.
 *
 * When you insert or delete into the middle of the tree all the paths that are "after" or "below" the
 * node that was created/deleted have no been changed by 1 index.
 *
 * This function will recompute the paths through the tree starting from a single sub sequence. This is faster
 * than computing the whole mission.
 *
 * @param sequence an action of type sequence that got re-arranged due to an insert, delete, or re-ordering
 * @param pathToSequence the node path to this sequence [0] for root
 * @param startFromIdx the index starting from which the sequence got re-arranged, default 0
 */
function mapPathsInSequence(
  sequence: ActionType<"sequence">,
  pathToSequence: NodePath,
  startFromIdx = 0
): NodeIndexMap {
  return findInSequence(sequence, pathToSequence, { startFromIdx }).reduce(
    (acc, value) => ({ ...acc, [value.action.actionUuid]: value.path }),
    {}
  );
}

/**
 * Make the same mutation on every action of type T. Mission object is marked as read only
 * to avoid having side effects outside the render cycle. DON'T CHANGE THIS.
 */
export function applyToActionsOfType<T extends keyof ActionArgs>(
  mission: Mission,
  actionType: T,
  mutation: (action: ActionType<T>, mission: Readonly<Mission>) => void
): Mission {
  findActions(mission, action => isActionType(action, actionType)).map(({ action }) =>
    mutation(action as ActionType<T>, mission)
  );
  return { ...mission };
}

export function createAction(
  mission: Mission,
  _nodeIndexMap: NodeIndexMap,
  action: Action,
  parentSequenceId: NodeId,
  idx: number,
  idMode: "generate" | "useExisting"
): { _nodeIndexMap: NodeIndexMap; missionSpec: Mission } {
  const parentPath = _nodeIndexMap[parentSequenceId] ?? [];
  // action doesn't exist yet so we find its parent
  const parent = getActionForPath(mission.actions, parentPath);
  if (!parent || !isActionType(parent.action, "sequence")) {
    throw new MissionException(
      `Parent sequence id must point to a sequence. Got ${parent?.action?.actionKey} for id ${parentSequenceId}`,
      mission
    );
  }
  const { actions } = parent.action.args.sequence;
  // when creating a new action always generate a new UUID
  const newAction = idMode === "generate" ? { ...action, actionUuid: uuidv4() } : action;
  actions.splice(idx, 0, newAction);

  /**
   * We perform a local recompute of the valid tree paths for the newly inserted actions parent
   * sequence starting from the index of the inserted action. After this index all action paths
   * will be shifted over by 1.
   */

  const newNodeIndexMap = {
    ..._nodeIndexMap,
    ...mapPathsInSequence(parent.action, parentPath, idx),
  };

  return {
    missionSpec: { ...mission },
    _nodeIndexMap: newNodeIndexMap,
  };
}

export function deleteAction(
  mission: Mission,
  _nodeIndexMap: NodeIndexMap,
  id: NodeId
): { _nodeIndexMap: NodeIndexMap; missionSpec: Mission } {
  const path = [..._nodeIndexMap[id]];
  const target = getActionForPath(mission.actions, path);
  if (!target || !isNonRootSearchResult(target)) {
    throw new MissionException(
      `cannot find parent sequence for action to delete for id ${id}`,
      mission
    );
  }
  // take off the last index from the path, this is our target idx to remove. The remaining path
  // points to the parent of that target
  const idxToDelete = path.pop()!;
  const { actions } = target.parentSequence.args.sequence;
  actions.splice(idxToDelete, 1);
  const newIndexMap = {
    ..._nodeIndexMap,
    ...mapPathsInSequence(target.parentSequence, path, idxToDelete),
  };

  delete newIndexMap[id];
  if (isActionType(target.action, "sequence")) {
    target.action.args.sequence.actions.forEach(action => {
      delete newIndexMap[action.actionUuid];
    });
  }

  return {
    missionSpec: { ...mission },
    _nodeIndexMap: newIndexMap,
  };
}

export function updateAction(
  mission: Mission,
  _nodeIndexMap: NodeIndexMap,
  toUpdate: Action,
  moveToIdx?: number
): { _nodeIndexMap: NodeIndexMap; missionSpec: Mission } {
  const path = _nodeIndexMap[toUpdate.actionUuid] ?? [];
  const target = getActionForPath(mission.actions, path);
  if (!target) {
    throw new MissionException(
      `cannot find action to update for id ${toUpdate.actionUuid}`,
      mission
    );
  }
  if (!isNonRootSearchResult(target)) {
    throw new MissionException(
      `cannot modify root sequence directly. Pass the id of one of its actions instead`,
      mission
    );
  }

  const { actions } = target.parentSequence.args.sequence;
  const actionIdx = path[path.length - 1];
  actions[actionIdx] = shallowMerge(target.action, toUpdate, {
    copyNulls: true,
    copyUndefineds: true,
  });
  let newIndexMap = _nodeIndexMap;
  if (moveToIdx != null) {
    if (moveToIdx < 0) throw new Error(`moveToIdx must be greater than 0 got ${moveToIdx}`);
    if (moveToIdx >= actions.length)
      throw new Error(
        `moveToIdx invalid. Got ${moveToIdx}. Must be less than parent sequence length of ${actions.length}`
      );
    const temp = actions[moveToIdx];
    actions[moveToIdx] = actions[actionIdx];
    actions[actionIdx] = temp;
    newIndexMap = {
      ..._nodeIndexMap,
      ...mapPathsInSequence(target.parentSequence, path, Math.min(actionIdx, moveToIdx)),
    };
  }

  return {
    missionSpec: { ...mission },
    _nodeIndexMap: newIndexMap,
  };
}

/**
 * Creates a map of action UUIDs to their paths in the action tree.
 * @param mission The mission containing the action tree
 * @returns Map of action UUIDs to their paths in the tree
 */
export const getNodeIndexMap = (mission: Mission) =>
  findActions(mission, () => true).reduce(
    (acc, value) => ({ ...acc, [value.action.actionUuid]: value.path }),
    {} as NodeIndexMap
  );

/**
 * Get the waypoint UUID from an action, regardless of its type.
 * If the action is a waypoint, returns the UUID of the position action within it.
 * Otherwise, returns the original action UUID.
 *
 * @param action The action to get the waypoint UUID from
 * @returns The UUID that should be used for mission planner operations
 */
export function getWaypointUuid(action: Action): string {
  if (isWaypoint(action)) {
    return getWaypointActions(action).position.actionUuid;
  }
  return action.actionUuid;
}
